import type { InfiniteData } from '@tanstack/react-query'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Tag } from '@/types/Tag'

// Add IDs without duplicates
export const unionTagIds = (
  baseIds: UUID[],
  addIds: UUID[] | undefined | null,
): UUID[] => {
  if (!addIds || addIds.length === 0) return baseIds.slice()
  const set = new Set<UUID>(baseIds)
  for (const id of addIds) set.add(id)
  return Array.from(set)
}

// Remove IDs from the list
export const removeTagIds = (
  baseIds: UUID[],
  removeIds: UUID[] | undefined | null,
): UUID[] => {
  if (!removeIds || removeIds.length === 0) return baseIds.slice()
  const remove = new Set<UUID>(removeIds)
  return baseIds.filter((id) => !remove.has(id))
}

/**
 * Order tags by their manual sort position (nulls last), falling back to
 * name for tags without a position.
 */
export const compareTagsByPosition = (a: Tag, b: Tag): number => {
  const aPos = a.position ?? Number.POSITIVE_INFINITY
  const bPos = b.position ?? Number.POSITIVE_INFINITY
  if (aPos !== bPos) return aPos < bPos ? -1 : 1
  return a.name.localeCompare(b.name)
}

export const selectTagPositionById = (
  data: InfiniteData<PaginatedResponse<Tag>, PaginateRequest>,
): Map<UUID, number> => {
  const positions = new Map<UUID, number>()
  for (const page of data.pages) {
    for (const tag of page.data) {
      if (tag.position != null) {
        positions.set(tag.id, tag.position)
      }
    }
  }
  return positions
}

/**
 * Match tags from one set to another by name (case-insensitive).
 * Useful for mapping source tags to org tags when copying files to library.
 *
 * @param sourceTagIds - Tag IDs from the source (e.g., pursuit file tags)
 * @param sourceTags - All tags from the source context (e.g., pursuit tags)
 * @param targetTags - All tags from the target context (e.g., org tags)
 * @returns Array of target tag IDs that match source tags by name
 */
export const matchTagsByName = (
  sourceTagIds: UUID[],
  sourceTags: Tag[] | undefined,
  targetTags: Tag[] | undefined,
): UUID[] => {
  if (!sourceTagIds || sourceTagIds.length === 0) return []
  if (!sourceTags || sourceTags.length === 0) return []
  if (!targetTags || targetTags.length === 0) return []

  // Build a map of source tag ID -> tag name (lowercase)
  const sourceTagNameMap = new Map<UUID, string>()
  for (const tag of sourceTags) {
    sourceTagNameMap.set(tag.id, tag.name.toLowerCase().trim())
  }

  // Build a map of target tag name (lowercase) -> tag ID
  const targetTagByName = new Map<string, UUID>()
  for (const tag of targetTags) {
    targetTagByName.set(tag.name.toLowerCase().trim(), tag.id)
  }

  // Match source tags to target tags by name
  const matchedTargetIds: UUID[] = []
  for (const sourceTagId of sourceTagIds) {
    const sourceName = sourceTagNameMap.get(sourceTagId)
    if (sourceName) {
      const targetId = targetTagByName.get(sourceName)
      if (targetId) {
        matchedTargetIds.push(targetId)
      }
    }
  }

  return matchedTargetIds
}
